import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import type { IInventoryType } from '@app/land-hold/inventory-type/models/InventoryType';
import { InventoryType } from '@app/land-hold/inventory-type/models/InventoryType';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';

export function useInventoryTypeRepository<T extends IInventoryType = IInventoryType>() {
    const command = () => {
        const hasError = useInjectErrorState();
        const store = (data: T, options?: FormOptions) => {
            const form = useForm<T>(data).precognition(
                InventoryType.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(InventoryType.routes().update, {
                        id,
                    }),
                    method: InventoryType.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(InventoryType.routes().destroy, {
                        id,
                    }),
                    method: InventoryType.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(InventoryType.routes().show, { id }) };
        };
        const list = () => {
            const { data: inventoryTypes, processing: fetchingInventoryTypes } = useFetchListData<InventoryType>(useRoute().build(InventoryType.routes().index), 'appSection@inventoryType::IndexInventoryTypesPage', (payload) => InventoryType.from(payload));
            return { inventoryTypes, fetchingInventoryTypes };
        };
        return {
            show,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
